import React, { useEffect } from 'react';
import { object } from 'prop-types';

import moment from 'moment';

import { capitalize, cloneDeep, isEmpty, isNil } from 'lodash';

// eslint-disable-next-line no-unused-vars
import hammerjs from 'hammerjs';

import {
    Chart,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartSeries,
    ChartSeriesItem,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartLegend,
    // ChartArea,
    // ChartTooltip,
    // ChartSeriesLabels,
} from '@progress/kendo-react-charts';

import clsx from 'clsx';

import classes from './InspectionReport.module.css';
import '@progress/kendo-theme-material/dist/all.css';
import { getTimeZoneAbbr } from '../helpers/timezones';
import InspectionReportGrid from './InspectionReportGrid';

const InspectionReport = React.forwardRef(({ report, ...props }, ref) => {
    useEffect(() => {
        if (report.reportData.sensorLogData && report.reportData.sensorLogData.SpaceTemperature.Label !== null) {
            report.reportData.sensorLogData.SpaceTemperature.TemperatureDetails?.forEach(item => item.Date = new Date(item.StartDate));
            report.reportData.sensorLogData.OutsideAirTemperature.TemperatureDetails?.forEach(item => item.Date = new Date(item.StartDate));
        }

        if (report.reportData.monthlySensorLogData && report.reportData.monthlySensorLogData.CurrentSpaceTemperature.Label !== null) {
            report.reportData.monthlySensorLogData.CurrentSpaceTemperature.TemperatureDetails?.forEach(item => item.Date = new Date(item.StartDate));
            report.reportData.monthlySensorLogData.CurrentOutsideAirTemperature.TemperatureDetails?.forEach(item => item.Date = new Date(item.StartDate));
        }

        if (!isEmpty(report.reportData.SiteMetrics)) {
            report.reportData.SiteMetrics.forEach(item => {
                if (!isEmpty(item.Data)) {
                    item.Data.forEach(item => {
                        item.Date = new Date(item.Date);
                    });
                }
            });
        }
    }, [report]);

    const convertDate = date => {
        const zone = moment.tz.guess();
        return moment(date).format('YYYY-MM-DD h:mm A') + ' ' + getTimeZoneAbbr(moment(date), zone);
    };

    const renderLogo = report => {
        if (report.reportData.CompanyLogoUrl) {
            return (
                <img 
                    src={report.reportData.CompanyLogoUrl} 
                    alt={report.reportData.CompanyName} 
                    height="5rem" 
                    crossOrigin="anonymous"
                />
            );
        }

        return (
            <h1>{report.reportData.CompanyName}</h1>
        );
    };

    const renderDot = priority => {
        if (!priority) return (<span className={classes.NoPriority} />);

        switch (priority.toLowerCase()) {
            case 'high':
                return (<span className={classes.HighPriority} />);
            case 'medium':
                return (<span className={classes.MediumPriority} />);
            case 'low':
                return (<span className={classes.LowPriority} />);
            default:
                return (<span className={classes.NoPriority} />);
        }
    };

    const renderMetric = item => {
        switch (item.Metric) {
            case 'overallSiteScore':
                return renderSiteScoreMetric(item);
            default:
                break;
        }
    };

    const renderSiteScoreMetric = item => {
        let deltaNumber = 0;
        let value = 'No Data';

        if (!isNil(item.Delta)) {
            deltaNumber = parseInt(item.Delta, 10);
        }

        if (!isNil(item.Value)) {
            value = `${item.Value}%`;
        }

        if (deltaNumber > 0) {
            return (
                <div className={clsx(classes.metrics, classes.metric_positive)}>
                    <div className={classes.score}>{value}</div>
                    <div className={clsx(classes.delta, classes.delta_positive)}>+{deltaNumber}</div>
                </div>
            );
        } else if (deltaNumber < 0) {
            return (
                <div className={clsx(classes.metrics, classes.metric_negative)}>
                    <div className={classes.score}>{value}</div>
                    <div className={clsx(classes.delta, classes.delta_negative)}>{deltaNumber}</div>
                </div>
            );
        } else {
            return (
                <div className={classes.metrics}>
                    <div className={classes.score}>{value}</div>
                    <div className={classes.delta}>{deltaNumber}</div>
                </div>
            );
        }
    };

    const renderTemperatureData = data => {
        let averageSpaceTemperature = null;
        let averageOutsideAirTemperature = null;
        let spaceDelta = 0;
        let outsideAirDelta = 0;
        const average = array => array.reduce((a, b) => a + b) / array.length;

        if (data.sensorLogData) {
            const currentSpaceValues = data.sensorLogData.SpaceTemperature.TemperatureDetails?.filter(d => moment(d.EnDate).isSameOrAfter(data.TargetDate.Start)).map(d => d.Value);
            const currentSpaceAverage = currentSpaceValues?.length > 0 ? average(currentSpaceValues) : 0;

            const currentOutsideValues = data.sensorLogData.OutsideAirTemperature.TemperatureDetails?.filter(d => moment(d.EndDate).isSameOrAfter(data.TargetDate.Start)).map(d => d.Value);
            const currentOutsideAverage = currentOutsideValues?.length > 0 ? average(currentOutsideValues) : 0;

            const priorSpaceValues = data.sensorLogData.SpaceTemperature.TemperatureDetails?.filter(d => moment(d.EnDate).isBefore(data.TargetDate.Start)).map(d => d.Value);
            const priorSpaceAverage = priorSpaceValues?.length > 0 ? average(priorSpaceValues) : 0;

            const priorOutsideValues = data.sensorLogData.OutsideAirTemperature.TemperatureDetails?.filter(d => moment(d.EndDate).isBefore(data.TargetDate.Start)).map(d => d.Value);
            const priorOutsideAverage = priorOutsideValues?.length > 0 ? average(priorOutsideValues) : 0;

            outsideAirDelta = Math.round((currentOutsideAverage - priorOutsideAverage) * 10)/10;
            spaceDelta = Math.round((currentSpaceAverage - priorSpaceAverage) * 10)/10;

            if (data.sensorLogData.OutsideAirTemperature.TemperatureDetails?.length !== 0) {
                averageOutsideAirTemperature = `${Math.round(currentOutsideAverage * 10)/10}°F`;
            }
            else {
                averageOutsideAirTemperature = 'N/A';
            }
            if (data.sensorLogData.SpaceTemperature.TemperatureDetails?.length !== 0) {
                averageSpaceTemperature = `${Math.round(currentSpaceAverage * 10)/10}°F`;
            }
            else {
                averageSpaceTemperature = 'N/A';
            }
        }
        else if (data.monthlySensorLogData) {
            averageOutsideAirTemperature = data.monthlySensorLogData.CurrentOutsideAirTemperature.Value;
            outsideAirDelta = Math.round((data.monthlySensorLogData.CurrentOutsideAirTemperature.Value - data.monthlySensorLogData.PriorOutsideAirTemperature.Value) * 100)/100;
            averageSpaceTemperature = data.monthlySensorLogData.CurrentSpaceTemperature.Value;
            spaceDelta = Math.round((data.monthlySensorLogData.CurrentSpaceTemperature.Value - data.monthlySensorLogData.PriorSpaceTemperature.Value) * 100)/100;
        }

        const renderAverageSpaceTemperature = () => {
            if (spaceDelta > 0) {
                return (
                    <div className={clsx(classes.metrics, classes.metric_positive)}>
                        <div className={classes.score}>{averageSpaceTemperature}</div>
                        <div className={clsx(classes.delta, classes.delta_positive)}>{spaceDelta}</div>
                        <div className={classes.temperatureLabel}>Average Space Temperature</div>
                    </div>
                );
            }
            else if (spaceDelta < 0) {
                return (
                    <div className={clsx(classes.metrics, classes.metric_positive)}>
                        <div className={classes.score}>{averageSpaceTemperature}</div>
                        <div className={clsx(classes.delta, classes.delta_negative)}>{spaceDelta}</div>
                        <div className={classes.temperatureLabel}>Average Space Temperature</div>
                    </div>
                );
            }
            else {
                return (
                    <div className={clsx(classes.metrics)}>
                        <div className={classes.score}>{averageSpaceTemperature}</div>
                        <div className={clsx(classes.delta)}>{spaceDelta}</div>
                        <div className={classes.temperatureLabel}>Average Space Temperature</div>
                    </div>
                );
            }
        };

        const renderAverageOutsideAirTemperature = () => {
            if (outsideAirDelta > 0) {
                return (
                    <div className={clsx(classes.metrics, classes.metric_positive)}>
                        <div className={classes.score}>{averageOutsideAirTemperature}</div>
                        <div className={clsx(classes.delta, classes.delta_positive)}>{outsideAirDelta}</div>
                        <div className={classes.temperatureLabel}>Average Outside Air Temperature</div>
                    </div>
                );
            }
            else if (outsideAirDelta < 0) {
                return (
                    <div className={clsx(classes.metrics, classes.metric_negative)}>
                        <div className={classes.score}>{averageOutsideAirTemperature}</div>
                        <div className={clsx(classes.delta, classes.delta_negative)}>{outsideAirDelta}</div>
                        <div className={classes.temperatureLabel}>Average Outside Air Temperature</div>
                    </div>
                );
            }
            else {
                return (
                    <div className={clsx(classes.metrics)}>
                        <div className={classes.score}>{averageOutsideAirTemperature}</div>
                        <div className={clsx(classes.delta)}>{outsideAirDelta}</div>
                        <div className={classes.temperatureLabel}>Average Outside Air Temperature</div>
                    </div>
                );
            }
        };

        return (
            <div>
                {renderAverageSpaceTemperature()}
                {renderAverageOutsideAirTemperature()}
            </div>
        );
    };

    const getSpaceTemperatureChartData = data => {
        if (data.sensorLogData) {
            const currentData = data.sensorLogData.SpaceTemperature.TemperatureDetails?.filter(d => moment(d.StartDate).isSameOrAfter(data.TargetDate.Start));
            return currentData;
        }
        else if (data.monthlySensorLogData?.CurrentSpaceTemperature) {
            return data.monthlySensorLogData.CurrentSpaceTemperature.TemperatureDetails;
        }
    };

    const getOutsideTemperatureChartData = data => {
        if (data.sensorLogData) {
            const currentData = data.sensorLogData.OutsideAirTemperature.TemperatureDetails?.filter(d => moment(d.StartDate).isSameOrAfter(data.TargetDate.Start));
            return currentData;
        }
        else if (data.monthlySensorLogData?.CurrentOutsideAirTemperature) {
            return data.monthlySensorLogData.CurrentOutsideAirTemperature.TemperatureDetails;
        }
    };

    const renderTimestamp = (timestamp) => {
        const zone = moment.tz.guess();
        return 'Generated ' + moment(timestamp).format('YYYY-MM-DD h:mm A') + ' ' + getTimeZoneAbbr(moment(timestamp), zone);
    };

    const renderMonthlyReport = (rule, label) => {
        let gridLabel = label.split(' ')[0];
        gridLabel = capitalize(gridLabel);

        let data = [];

        const { currentEquipmentImpactionDbResult,
            currentSystemInspectionDbResult,
            priorEquipmentInspectionDbResult,
            priorSystemInspectionDbResult } = report.reportData;

        if (gridLabel === 'Equipment') {
            let currentEquipment = currentEquipmentImpactionDbResult.filter(item => item.RuleId === rule.RuleId);
            let priorEquipment = priorEquipmentInspectionDbResult.filter(item => item.RuleId === rule.RuleId);

            let distinctEquipment = [];

            priorEquipment.forEach(item => {
                item.priorFault = item.Fault;
                item.priorLastFaultTime = item.LastFaultTime;
                item.priorTotalHours = item.TotalHours;

                distinctEquipment.push(item);
            });

            currentEquipment.forEach(item => {
                item.currentFault = item.Fault;
                item.currentLastFaultTime = item.LastFaultTime;
                item.currentTotalHours = item.TotalHours;

                const index = distinctEquipment.findIndex(equipment => equipment.ImpactedItemId === item.ImpactedItemId);

                if (index >= 0) {
                    distinctEquipment[index] = { ...distinctEquipment[index], ...item };
                } else {
                    if (item.Fault) {
                        item.priorFault = false;
                        distinctEquipment.push(item);
                    }
                }
            });

            distinctEquipment = distinctEquipment.filter(item => item.Fault || item.priorFault);

            data = [...distinctEquipment];

        } else {
            const currentSystems = currentSystemInspectionDbResult.filter(item => item.RuleId === rule.RuleId);
            const priorSystems = priorSystemInspectionDbResult.filter(item => item.RuleId === rule.RuleId);
            let distinctSystems = [];

            priorSystems.forEach(item => {
                item.priorFault = item.Fault;
                item.priorLastFaultTime = item.LastFaultTime;
                item.priorTotalHours = item.TotalHours;

                distinctSystems.push(item);
            });

            currentSystems.forEach(item => {
                item.currentFault = item.Fault;
                item.currentLastFaultTime = item.LastFaultTime;
                item.currentTotalHours = item.TotalHours;

                const index = distinctSystems.findIndex(system => system.ImpactedItemId === item.ImpactedItemId);

                if (index >= 0) {
                    distinctSystems[index] = { ...distinctSystems[index], ...item };
                } else {
                    if (item.Fault) {
                        item.priorFault = false;
                        distinctSystems.push(item);
                    }
                }
            });

            distinctSystems = distinctSystems.filter(item => item.Fault || item.priorFault);

            data = [...distinctSystems];
        }

        return (
            <InspectionReportGrid data={data} label={gridLabel} />
        );
    };

    const renderAverageData = item => {
        let dataSet = cloneDeep(item.Data);

        let delta = item.Delta;
        let value = item.Value;

        if (delta === null) {
            return [];
        }

        if (delta.includes('+')) {
            delta = delta.substring(1);
            delta = parseInt(delta);

            value = value - delta;
        } else if (delta.includes('-')) {
            delta = delta.substring(1);
            delta = parseInt(delta);
            value = value + delta;
        }

        dataSet.forEach(item => {
            item.Value = value;
        });

        return dataSet;
    };

    //const renderCurrentFaultCount = ruleId => {
    //    const fault = report.reportData.FaultCountsByRule.find(item => item.RuleId === ruleId);

    //    if (fault) {
    //        return fault.CurrentFaultCount;
    //    }
    //};

    //const renderPreviousFaultCount = ruleId => {
    //    const fault = report.reportData.FaultCountsByRule.find(item => item.RuleId === ruleId);

    //    if (fault) {
    //        return fault.PreviouFaultCount;
    //    }
    //};

    // const renderDonutChart = (current, prior) => {
    //     const labelContent = (e) => `${e.value.toString().includes('.') ? parseFloat(e.value).toFixed(2) : e.value} Hours`;

    //     let data = [{ name: 'Prior', data: [] }, { name: 'Current', data: [] }];

    //     let currentOccupiedTotal = 0;
    //     let currentUnoccupiedTotal = 0;
    //     let currentOffTotal = 0;

    //     let priorOccupiedTotal = 0;
    //     let priorUnoccupiedTotal = 0;
    //     let priorOffTotal = 0;

    //     current.forEach(item => {
    //         if (item.TotalOccupied) currentOccupiedTotal = currentOccupiedTotal + item.TotalOccupied;
    //         if (item.TotalUnoccupied) currentUnoccupiedTotal = currentUnoccupiedTotal + item.TotalUnoccupied;
    //         if (item.TotalOffTime) currentOffTotal = currentOffTotal + item.TotalOffTime;
    //     });

    //     prior.forEach(item => {
    //         if (item.TotalOccupied) priorOccupiedTotal = priorOccupiedTotal + item.TotalOccupied;
    //         if (item.TotalUnoccupied) priorUnoccupiedTotal = priorUnoccupiedTotal + item.TotalUnoccupied;
    //         if (item.TotalOffTime) priorOffTotal = priorOffTotal + item.TotalOffTime;
    //     });

    //     data[1].data = [{ category: 'Occupied', value: currentOccupiedTotal, color: '#5274fa' }, { category: 'Unoccupied', value: currentUnoccupiedTotal, color: '#46a2af' }, { category: 'Off', value: currentOffTotal, color: '#f56a6d' }];
    //     data[0].data = [{ category: 'Occupied', value: priorOccupiedTotal, color: '#5274fa' }, { category: 'Unoccupied', value: priorUnoccupiedTotal, color: '#46a2af' }, { category: 'Off', value: priorOffTotal, color: '#f56a6d' }];

    //     const mapSeries = (series, index, array) => (
    //         <ChartSeriesItem
    //             type="donut"
    //             startAngle={150}
    //             name={series.name}
    //             data={series.data}
    //             field="value"
    //             padding={0}
    //             margin={10}
    //             categoryField="category"
    //             colorField="color"
    //         >
    //             <ChartSeriesLabels
    //                 background="white"
    //                 content={labelContent}
    //             />
    //         </ChartSeriesItem>
    //     );

    //     const renderTooltip = (context) => {
    //         const { category, series, value } = context.point || context;
    //         return (
    //             <div>
    //                 {category} ({series.name}): {value.toString().includes('.') ? parseFloat(value).toFixed(2) : value}Hours
    //             </div>
    //         );
    //     };

    //     return (
    //         <Chart style={{width: '30rem', height: '25rem'}}>
    //             <ChartTooltip render={renderTooltip} />
    //             <ChartArea background="none" />
    //             <ChartSeries>{data.map(mapSeries)}</ChartSeries>
    //             <ChartLegend visible={false} />
    //         </Chart>
    //     );
    // };

    // const renderRuntimeBarChart = current => {
    //     let occupied = [];
    //     let unoccupied = [];
    //     let off = [];

    //     current.forEach(item => {
    //         if (item.TotalOccupied) occupied.push(item.TotalOccupied);
    //         if (item.TotalUnoccupied) unoccupied.push(item.TotalUnoccupied);
    //         if (item.TotalOffTime) off.push(item.TotalOffTime);
    //     });

    //     return (
    //         <Chart seriesColors={['#5274fa', '#46a2af', '#f56a6d']} style={{ width: '28rem', height: '28rem' }}>
    //             <ChartLegend position="top" orientation="horizontal" />
    //             <ChartSeries>
    //                 <ChartSeriesItem
    //                     stack={true}
    //                     axis="Hours"
    //                     name="Occupied"
    //                     type="column"
    //                     data={occupied}
    //                 />
    //                 <ChartSeriesItem
    //                     axis="Hours"
    //                     name="Unoccupied"
    //                     type="column"
    //                     data={unoccupied}
    //                 />
    //                 <ChartSeriesItem
    //                     axis="Hours"
    //                     name="Off"
    //                     type="column"
    //                     data={off}
    //                 />
    //             </ChartSeries>
    //             <ChartValueAxis>
    //                 <ChartValueAxisItem
    //                     key={'Hours'}
    //                     name={'Hours'}
    //                     min={0}
    //                     max={24}
    //                     labels={{
    //                         visible: false,
    //                     }}
    //                 />
    //             </ChartValueAxis>
    //         </Chart>
    //     );
    // };

    // const renderRuntimeMetrics = (current, previous) => {
    //     let occupiedHours = 0;
    //     let unoccupiedHours = 0;
    //     let offHours = 0;

    //     let priorOccupiedHours = 0;
    //     let priorUnoccupiedHours = 0;
    //     let priorOffHours = 0;

    //     let occupiedDelta = 0;
    //     let unoccupiedDelta = 0;
    //     let offDelta = 0;

    //     current.forEach(item => {
    //         if (item.TotalOccupied) occupiedHours = occupiedHours + item.TotalOccupied;
    //         if (item.TotalUnoccupied) unoccupiedHours = unoccupiedHours + item.TotalUnoccupied;
    //         if (item.TotalOffTime) offHours = offHours + item.TotalOffTime;
    //     });

    //     previous.forEach(item => {
    //         if (item.TotalOccupied) priorOccupiedHours = priorOccupiedHours + item.TotalOccupied;
    //         if (item.TotalUnoccupied) priorUnoccupiedHours = priorUnoccupiedHours + item.TotalUnoccupied;
    //         if (item.TotalOffTime) priorOffHours = priorOffHours + item.TotalOffTime;
    //     });

    //     occupiedDelta = occupiedHours - priorOccupiedHours;
    //     unoccupiedDelta = unoccupiedHours - priorUnoccupiedHours;
    //     offDelta = offHours - priorOffHours;

    //     return (
    //         <div className={classes.Analytics_row_metrics_runtime}>
    //             <div className={classes.RuntimeTitle}>Occupied Run Time</div>
    //             <div className={occupiedDelta > 0 ? clsx(classes.runtime, classes.runtime_positive) : occupiedDelta < 0 ? clsx(classes.runtime, classes.runtime_negative) : classes.runtime}>
    //                 <div className={classes.runtimeHours}>{`${occupiedHours.toString().includes('.') ? parseFloat(occupiedHours).toFixed(2) : occupiedHours}hrs`}</div>
    //                 <div className={occupiedDelta > 0 ? clsx(classes.deltaHours, classes.positive) : occupiedDelta < 0 ? clsx(classes.deltaHours, classes.negative) : classes.deltaHours}>{`${occupiedDelta.toString().includes('.') ? (occupiedDelta > 0 ? '+' + parseFloat(occupiedDelta).toFixed(2) : parseFloat(occupiedDelta).toFixed(2)) : (occupiedDelta > 0 ? '+' + occupiedDelta : occupiedDelta)}hrs`}</div>
    //             </div>
    //             <div className={classes.RuntimeTitle}>Unoccupied Run Time</div>
    //             <div className={unoccupiedDelta > 0 ? clsx(classes.runtime, classes.runtime_positive) : unoccupiedDelta < 0 ? clsx(classes.runtime, classes.runtime_negative) : classes.runtime}>
    //                 <div className={classes.runtimeHours}>{`${unoccupiedHours.toString().includes('.') ? parseFloat(unoccupiedHours).toFixed(2) : unoccupiedHours}hrs`}</div>
    //                 <div className={unoccupiedDelta > 0 ? clsx(classes.deltaHours, classes.positive) : unoccupiedDelta < 0 ? clsx(classes.deltaHours, classes.negative) : classes.deltaHours}>{`${unoccupiedDelta.toString().includes('.') ? (unoccupiedDelta > 0 ? '+' + parseFloat(unoccupiedDelta).toFixed(2) : parseFloat(unoccupiedDelta).toFixed(2)) : (unoccupiedDelta > 0 ? '+' + unoccupiedDelta : unoccupiedDelta)}hrs`}</div>
    //             </div>
    //             <div className={classes.RuntimeTitle}>Off Time</div>
    //             <div className={offDelta > 0 ? clsx(classes.runtime, classes.runtime_positive) : offDelta < 0 ? clsx(classes.runtime, classes.runtime_negative) : classes.runtime}>
    //                 <div className={classes.runtimeHours}>{`${offHours.toString().includes('.') ? parseFloat(offHours).toFixed(2) : offHours}hrs`}</div>
    //                 <div className={offDelta > 0 ? clsx(classes.deltaHours, classes.positive) : offDelta < 0 ? clsx(classes.deltaHours, classes.negative) : classes.deltaHours}>{`${offDelta.toString().includes('.') ? (offDelta > 0 ? '+' + parseFloat(offDelta).toFixed(2) : parseFloat(offDelta).toFixed(2)) : (offDelta > 0 ? '+' + offDelta : offDelta)}hrs`}</div>
    //             </div>
    //         </div>
    //     );
    // };

    return (
        <React.Fragment>
            <style>
                {
                    `
                        @font-face {
                            font-family: "DejaVu Sans";
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans.ttf") format("truetype");
                        }

                        @font-face {
                            font-family: "DejaVu Sans";
                            font-weight: bold;
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans-Bold.ttf") format("truetype");
                        }

                        @font-face {
                            font-family: "DejaVu Sans";
                            font-style: italic;
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf") format("truetype");
                        }

                        @font-face {
                            font-family: "DejaVu Sans";
                            font-weight: bold;
                            font-style: italic;
                            src: url("https://kendo.cdn.telerik.com/2017.2.621/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf") format("truetype");
                        }
                    `
                }
            </style>
            <div ref={ref} className={classes.InspectionReport}>
                <div className={classes.Header}>
                    {renderLogo(report)}
                    <div>
                        <h2>Inspection Report</h2>
                        <div className={classes.Timestamp}>{renderTimestamp(report.timestamp)}</div>
                    </div>
                </div>
                <div className={classes.Overview}>
                    <div className={classes.Overview_topRow}>
                        <div className={classes.Overview_title}>
                            <p>
                                {report.siteName}
                            </p>
                            <span>
                                {report.clientName}
                            </span>
                        </div>
                        <div className={classes.Overview_dateRange}>
                            <div className={classes.Overview_dateRange_title}>
                                Current
                            </div>
                            <div className={classes.Overview_dateRange_label}>
                                <div>
                                    {`From: ${convertDate(report.reportData.TargetDate.Start)}`}
                                </div>
                                <div>
                                    {`To: ${convertDate(report.reportData.TargetDate.End)}`}
                                </div>
                            </div>
                            <div className={classes.Overview_dateRange_title}>
                                Previous
                            </div>
                            <div className={classes.Overview_dateRange_label}>
                                <div>
                                    {`From: ${convertDate(report.reportData.PriorDate.Start)}`}
                                </div>
                                <div>
                                    {`To: ${convertDate(report.reportData.PriorDate.End)}`}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={classes.Overview_bottomRow}>
                        {report.reportData.Description}
                    </div>
                </div>
                <div className={classes.Summary}>
                    <div className={classes.Section_title}>
                        Summary
                    </div>
                    {
                        report.reportData.Summary.map((item, index) => {
                            return (
                                <div className={classes.Summary_row} key={index}>
                                    <div className={classes.Summary_row_content}>
                                        <p className={classes.label}>{item.Label}</p>
                                        <p>
                                            {
                                                item.Value ?
                                                    `${item.Value} targets failed the specified criteria` :
                                                    `All targets passed the specified criteria`
                                            }
                                        </p>
                                    </div>
                                    <div className={classes.Summary_row_priority}>
                                        {renderDot(item.PriorityName)}
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
                <div className={clsx(classes.Analytics)}>
                    <div className={classes.Section_title}>
                        Site Metrics
                    </div>
                    {
                        report.reportData.SiteMetrics.map((item, index) => {
                            return (
                                <div className={classes.Analytics_row} key={index}>
                                    <div className={classes.Analytics_row_metrics}>
                                        <p>{item.Label}</p>
                                        {renderMetric(item)}
                                    </div>
                                    <div className={classes.Analytics_row_graph}>
                                        <Chart style={{ height: 280, width: 400 }}>
                                            <ChartLegend position="bottom" orientation="horizontal" />
                                            <ChartValueAxis>
                                                <ChartValueAxisItem title={{ text: 'Site Score' }} min={0} max={100} />
                                            </ChartValueAxis>
                                            <ChartCategoryAxis>
                                                <ChartCategoryAxisItem
                                                    baseUnit="auto"
                                                    maxDivisions={12}
                                                    labels={{ visible: true }}
                                                />
                                            </ChartCategoryAxis>
                                            <ChartSeries>
                                                <ChartSeriesItem
                                                    type="area"
                                                    name="Site Score"
                                                    field="Value"
                                                    categoryField="Date"
                                                    tooltip={{ visible: true }}
                                                    data={item.Data}
                                                    // eslint-disable-next-line react/style-prop-object
                                                    style="smooth"
                                                />
                                                <ChartSeriesItem
                                                    type="line"
                                                    name="Previous Average"
                                                    field="Value"
                                                    categoryField="Date"
                                                    color="black"
                                                    markers={{ visible: false }}
                                                    dashType="dash"
                                                    tooltip={{ visible: true }}
                                                    data={renderAverageData(item)}
                                                    // eslint-disable-next-line react/style-prop-object
                                                    style="smooth"
                                                />
                                            </ChartSeries>
                                        </Chart>
                                    </div>
                                </div>
                            );
                        })
                    }
                    {
                        (report.reportData.sensorLogData && report.reportData.sensorLogData.SpaceTemperature.Label !== null) ||
                        report.reportData.monthlySensorLogData ?
                            <div className={classes.Analytics_row}>
                                <div className={classes.Analytics_row_metrics}>
                                    <p>Temperature</p>
                                    {renderTemperatureData(report.reportData)}
                                </div>
                                <div className={classes.Analytics_row_graph}>
                                    <Chart style={{ height: 280, width: 400 }}>
                                        <ChartLegend position="bottom" orientation="horizontal" />
                                        <ChartValueAxis>
                                            <ChartValueAxisItem title={{ text: 'Temperature' }} />
                                        </ChartValueAxis>
                                        <ChartCategoryAxis>
                                            <ChartCategoryAxisItem
                                                baseUnit="auto"
                                                maxDivisions={12}
                                                labels={{ visible: true }}
                                            />
                                        </ChartCategoryAxis>
                                        <ChartSeries>
                                            <ChartSeriesItem
                                                type="area"
                                                name="Space Temperature"
                                                field="Value"
                                                categoryField="Date"
                                                color="red"
                                                tooltip={{ visible: true }}
                                                data={getSpaceTemperatureChartData(report.reportData)}
                                                spacing={0.25}
                                            />
                                            <ChartSeriesItem
                                                type="area"
                                                name="Outside Air Temperature"
                                                field="Value"
                                                categoryField="Date"
                                                tooltip={{ visible: true }}
                                                data={getOutsideTemperatureChartData(report.reportData)}
                                                spacing={0.25}
                                                color="blue"
                                            />
                                        </ChartSeries>
                                    </Chart>
                                </div>
                            </div>
                            :
                            null
                    }
                    {/* <div className={'page-break'} style={{display: 'none'}} />
                    {
                        report.reportData.CurrentRuntimeSensorDetails || report.reportData.PriorRuntimeSensorDetails ?
                            <div className={classes.Analytics_row_runtime}>
                                {renderRuntimeMetrics(report.reportData.CurrentRuntimeSensorDetails, report.reportData.PriorRuntimeSensorDetails)}
                                <div className={classes.Runtime_row_graph}>
                                    <div>
                                        {renderDonutChart(report.reportData.CurrentRuntimeSensorDetails, report.reportData.PriorRuntimeSensorDetails)}
                                    </div>
                                    <div>
                                        {renderRuntimeBarChart(report.reportData.CurrentRuntimeSensorDetails)}
                                    </div>
                                </div>
                            </div>
                            :
                            null
                    } */}
                </div>
                <div className="page-break" style={{ display: 'none' }} />
                <div className={classes.IssueLog}>
                    <div className={classes.Section_title}>
                        Issue Log
                    </div>
                    {
                        report.reportData.IssueLog.filter(item => item.Label === 'EQUIPMENT ISSUES' || item.Label === 'SYSTEM ISSUES').map((log, index) => {
                            return (
                                <div key={index}>
                                    {index !== 0 ? 
                                        <div className={'page-break'} style={{ display: 'none' }} />
                                        : null 
                                    }
                                    <div className={classes.IssueLog_log} key={index}>
                                        <div className={classes.IssueLog_log_header}>
                                            {log.Label}
                                        </div>
                                        {
                                            log.Data?.length ?
                                                log.Data.map((rule, innerIndex) => {
                                                    return (
                                                        <React.Fragment key={innerIndex}>
                                                            {innerIndex !== 0 ? <div className={'page-break'} style={{display: 'none'}} /> : null}
                                                            <div className={classes.IssueLog_log_rule}>
                                                                <div className={classes.IssueLog_log_rule_content}>
                                                                    <div className={classes.rule_title}>
                                                                        {rule.Label}
                                                                    </div>
                                                                </div>
                                                                <div className={classes.IssueLog_log_rule_metrics}>
                                                                    {
                                                                        report.reportData.currentEquipmentImpactionDbResult ?
                                                                            <React.Fragment>
                                                                                <div className={classes.IssueLog_comparison}>
                                                                                    <span className={classes.rule_value}>Current: </span>
                                                                                    <span className={classes.rule_value}>{rule.CurrentCount || '0'}</span>
                                                                                    {renderDot(rule.Priority)}
                                                                                </div>
                                                                                <div className={classes.IssueLog_comparison_previous}>
                                                                                    <span className={classes.rule_value}>Previous: </span>
                                                                                    <span className={classes.rule_value}>{rule.PriorCount || '0'}</span>
                                                                                </div>
                                                                            </React.Fragment>
                                                                            :
                                                                            <React.Fragment>
                                                                                <div className={classes.IssueLog_comparison_single}>
                                                                                    <span className={classes.rule_value}>{rule.Value}</span>
                                                                                    {renderDot(rule.Priority)}
                                                                                </div>
                                                                            </React.Fragment>
                                                                    }
                                                                </div>
                                                                <div className={classes.IssueLog_rows}>
                                                                    {
                                                                        rule.Data.map((ruleItem, ruleItemIndex) => {
                                                                            return (
                                                                                <div className={ruleItem.Label.toLowerCase().includes('impacted') ? classes.rule_item_row_grid : classes.rule_item_row} key={ruleItemIndex}>
                                                                                    <div className={classes.rule_item_row_title}>{ruleItem.Label}</div>
                                                                                    <div className={classes.rule_item_row_text}>{ruleItem.Label.toLowerCase().includes('impacted') && report.reportData.currentEquipmentImpactionDbResult ? renderMonthlyReport(rule, log.Label) : ruleItem.Value}</div>
                                                                                </div>
                                                                            );
                                                                        })
                                                                    }
                                                                </div>
                                                            </div>
                                                        </React.Fragment>
                                                    );
                                                }) :
                                                (
                                                    <div className={classes.IssueLog_log_noRules}>
                                                        {`No ${log.Label.toLowerCase()}`}
                                                    </div>
                                                )
                                        }
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>
                <br />
                <div id="pageFooter" className={classes.Footer}>
                    <div className={classes.NoPriorityFooter}><span className={classes.NoPriority} />&nbsp;-&nbsp;No Issues</div>
                    <div className={classes.LowPriorityFooter}><span className={classes.LowPriority} />&nbsp;-&nbsp;Low Priority</div>
                    <div className={classes.MediumPriorityFooter}><span className={classes.MediumPriority} />&nbsp;-&nbsp;Medium Priority</div>
                    <div className={classes.HighPriorityFooter}><span className={classes.HighPriority} />&nbsp;-&nbsp;High Priority</div>
                    <div className={classes.ScoreCalucationDescription}><span>With Building Optimization Broker's default scoring, each equipment or system starts with a score of 100%. As rules are evaluated, that score is decremented based on the priority of faults occurring on a piece of equipment or system. Low Priority faults impact an equipment or system's score less than a Medium or High Priority fault. </span></div>
                </div>
            </div>
        </React.Fragment>
    );
});

InspectionReport.defaultProps = {
    report: {}
};

InspectionReport.propTypes = {
    report: object
};

export default InspectionReport;
